import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, InputAdornment, styled, TextField, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { TFormType, TProjectFormData } from 'src/@types';
import { IProject } from 'src/@types/apiResponseTypes';
import { projectApi } from 'src/api';
import DepartmentSelect from 'src/components/DepartmentSelect';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/FormProvider';
import RHFDatePicker from 'src/components/hook-form/RHFDatePicker';
import RHFEmployeeSelect from 'src/components/RHFEmployeeSelect';
import { useStores } from 'src/models';
import { COMMON_CODE_NAME, PROJECT_STATUS_CD, PROJECT_TYPE_CD } from 'src/static/constants';
import projectFormValidationSchema from 'src/static/yup/projectFormValidationSchema';
import { convertCommonCodeToOptions } from 'src/utils/convertCodeToOtions';
import { getProjectFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import { numberToSimpleKoreanCurrency } from 'src/utils/koreanCurrency';

import AssignDepositsSection from './AssignDepositsSection';
import ProjectRoundSalesSection from './ProjectRoundSalesSection';
import PurchaseAssignmentSection from './PurchaseAssignmentSection';
import PurchaseOtherSection from './PurchaseOtherSection';
import RHFClientSelect from './RHFClientSelect';

export interface IProjectFormProps {
  readonly?: boolean;
  formId: string;
  formType: TFormType;
  project?: IProject;
  onComplete?: () => void;
}

/**
 * ## ProjectForm 설명
 * - 프로젝트 등록/수정 폼
 * - PROJECT_TYPE_CD에 따라 표시되는 섹션이 다름
 *   - 과제(ASSIGNMENT): 매입내역(purchaseAssignment)만 표시
 *   - 그 외: 기성내역 + 매입내역(purchaseOther)
 */
function ProjectForm({ readonly, formId, formType, project, onComplete }: IProjectFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { commonCodeStore } = useStores();
  const resolver = yupResolver(projectFormValidationSchema);

  const projectTypeOptions = convertCommonCodeToOptions(
    commonCodeStore.getCCodeByName(COMMON_CODE_NAME.PROJECT_TYPE_CD),
  );
  const projectStatusOptions = convertCommonCodeToOptions(
    commonCodeStore.getCCodeByName(COMMON_CODE_NAME.PROJECT_STATUS_CD),
  );

  // useForm methods
  const methods = useForm<TProjectFormData>({
    defaultValues: getProjectFormDefaultValues(project),
    resolver,
    mode: 'all',
  });

  // 프로젝트 관리 ID 생성 로딩 상태
  const [isGeneratingId, setIsGeneratingId] = useState(false);

  // 프로젝트 구분 watch
  const watchProjectTypeCd = useWatch({ control: methods.control, name: 'projectTypeCd' });
  // 프로젝트 상태 watch
  const watchProjectStatusCd = useWatch({ control: methods.control, name: 'projectStatusCd' });
  // 프로젝트 관리 ID watch
  const watchProjectMngId = useWatch({ control: methods.control, name: 'projectMngId' });

  // 과제 여부 확인 (프로젝트 구분 코드 === 과제)
  const isAssignment = useMemo(
    () => Number(watchProjectTypeCd) === PROJECT_TYPE_CD.ASSIGNMENT,
    [watchProjectTypeCd],
  );

  // 프로젝트 관리 ID 필드 표시 여부
  const shouldShowProjectMngId = useMemo(() => {
    const statusCd = Number(watchProjectStatusCd);

    // 계약/종료/진행 상태 → 무조건 표시
    if (
      statusCd === PROJECT_STATUS_CD.CONTRACT ||
      statusCd === PROJECT_STATUS_CD.END ||
      statusCd === PROJECT_STATUS_CD.IN_PROGRESS
    ) {
      return true;
    }

    // 제안/제안탈락 → 생성된 ID가 있으면 표시
    if (statusCd === PROJECT_STATUS_CD.PROPOSAL || statusCd === PROJECT_STATUS_CD.PROPOSAL_FAILED) {
      return !!watchProjectMngId;
    }

    // 상태가 없거나 기타 → 숨김
    return false;
  }, [watchProjectStatusCd, watchProjectMngId]);

  // 생성 버튼 표시 여부 (계약/진행/종료 상태 + ID 없을 때)
  const shouldShowGenerateButton = useMemo(() => {
    const statusCd = Number(watchProjectStatusCd);
    const canGenerateInStatus =
      statusCd === PROJECT_STATUS_CD.CONTRACT ||
      statusCd === PROJECT_STATUS_CD.IN_PROGRESS ||
      statusCd === PROJECT_STATUS_CD.END;

    return canGenerateInStatus && !readonly && !watchProjectMngId;
  }, [watchProjectStatusCd, readonly, watchProjectMngId]);

  // 프로젝트 관리 ID 필드는 항상 읽기 전용 (직접 입력 불가)
  const isProjectMngIdReadOnly = true;

  // projectMngId가 DB에 저장된 것인지 판단
  const isPersistedProjectMngId = !!project?.projectMngId;

  // projectTypeCd 변경 감지 및 임시 ID 초기화
  useEffect(() => {
    // DB에 저장되지 않은 화면상의 임시 ID만 초기화
    if (!isPersistedProjectMngId && watchProjectMngId) {
      methods.setValue('projectMngId', '');
      enqueueSnackbar('프로젝트 구분이 변경되어 관리 ID가 초기화되었습니다.', {
        variant: 'info',
      });
    }
  }, [watchProjectTypeCd]);

  // 프로젝트 관리 ID 생성 버튼 핸들러
  const handleGenerateProjectMngId = async () => {
    if (!watchProjectTypeCd) {
      enqueueSnackbar('프로젝트 구분을 먼저 선택해주세요.', { variant: 'warning' });
      return;
    }

    setIsGeneratingId(true);
    try {
      const result = await projectApi.getProjectManageId(Number(watchProjectTypeCd));
      if (result?.data) {
        methods.setValue('projectMngId', result.data);
        enqueueSnackbar('프로젝트 관리 ID가 생성되었습니다.', { variant: 'success' });
      } else {
        enqueueSnackbar('프로젝트 관리 ID 생성에 실패했습니다.', { variant: 'error' });
      }
    } catch (error) {
      console.error('프로젝트 관리 ID 생성 실패:', error);
      enqueueSnackbar('프로젝트 관리 ID 생성 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setIsGeneratingId(false);
    }
  };

  // 등록 혹은 수정 시 처리 함수
  const onSubmit = async (projectInfo: TProjectFormData) => {
    try {
      let success = false;

      // 프로젝트 구분에 따라 해당하지 않는 필드 초기화
      const submitData = { ...projectInfo };
      const typeCd = submitData.projectTypeCd;

      if (Number(typeCd) === PROJECT_TYPE_CD.ASSIGNMENT) {
        // 과제: 기성내역, 그 외 매입내역 초기화 (입금내역/과제매입내역 유지)
        submitData.projectRoundSales = [];
        submitData.purchaseOthers = [];
      } else {
        // 그 외: 과제용 매입내역 / 입금내역 초기화
        submitData.purchaseAssignments = [];
        submitData.assignDeposits = [];
      }

      if (project?.projectSid && formType === 'UPDATE') {
        const result = await projectApi.updateProject(project.projectSid, {
          ...project,
          ...submitData,
        } as TProjectFormData);
        if (result.success) {
          enqueueSnackbar('프로젝트가 수정되었습니다.', { variant: 'success' });
          success = true;
        } else {
          enqueueSnackbar(result.errorMessage, { variant: 'error' });
        }
      } else {
        const result = await projectApi.createProject(submitData);
        if (result.success) {
          enqueueSnackbar('프로젝트가 등록되었습니다.', { variant: 'success' });
          success = true;
        } else {
          enqueueSnackbar(result.errorMessage, { variant: 'error' });
        }
      }

      // 성공 시에만 모달 닫기
      if (success && onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('요청 처리 중 오류가 발생했습니다.', { variant: 'error' });
    }
  };

  return (
    <StyledFormProvider
      methods={methods}
      onSubmit={methods.handleSubmit(onSubmit, (error) => {
        console.error(error);
      })}
      formId={formId}
    >
      {/* 기본정보 */}
      <StyledInnerFormContainer>
        <StyledLabel>기본정보</StyledLabel>
        <RowContainer>
          <RHFTextField name="projectNm" label="프로젝트명" inputProps={{ readOnly: readonly }} />
        </RowContainer>
        <RowContainer>
          <RHFEmployeeSelect
            name="bo"
            label="사업PM"
            multiple={false}
            returnType="number"
            readOnly={readonly}
          />
          <RHFEmployeeSelect
            name="pmList"
            label="개발PM"
            multiple={false}
            alwaysArray
            readOnly={readonly}
          />
        </RowContainer>
        <RowContainer>
          <Controller
            control={methods.control}
            name="deprtCd"
            render={({ field, fieldState: { error } }) => (
              <DepartmentSelect
                size="medium"
                label="프로젝트 담당 부서"
                value={field.value || undefined}
                onChange={(value) => {
                  field.onChange(value || undefined);
                }}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <RHFClientSelect name="customerCmpy" label="고객사" readOnly={readonly} />
        </RowContainer>
        <RowContainer>
          <RHFSelect
            name="projectTypeCd"
            label="구분"
            options={projectTypeOptions}
            inputProps={{ readOnly: readonly }}
          />
          <RHFSelect
            name="projectStatusCd"
            label="프로젝트 상태"
            options={projectStatusOptions}
            inputProps={{ readOnly: readonly }}
          />
        </RowContainer>
        <RowContainer>
          <RHFDatePicker name="startDay" label="시작일" readOnly={readonly} />
          <RHFDatePicker name="endDay" label="종료일" readOnly={readonly} />
        </RowContainer>
        <RowContainer>
          <RHFEmployeeSelect name="mngList" label="참여자" readOnly={readonly} />
          <RHFEmployeeSelect name="alertList" label="Slack 알림 담당자" readOnly={readonly} />
        </RowContainer>
      </StyledInnerFormContainer>

      {/* 계약정보 */}
      <StyledInnerFormContainer>
        <StyledLabel>계약정보</StyledLabel>
        <RowContainer>
          <RHFTextField name="cntrctNm" label="계약명" inputProps={{ readOnly: readonly }} />
          <RHFClientSelect name="contractCmpy" label="계약사" readOnly={readonly} />
        </RowContainer>
        <RowContainer>
          {shouldShowProjectMngId && (
            <Controller
              name="projectMngId"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="프로젝트 관리 ID"
                  placeholder={
                    !watchProjectMngId && shouldShowGenerateButton
                      ? '구분 선택 후 생성 버튼을 클릭하세요'
                      : ''
                  }
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  disabled={!!watchProjectMngId} // ID가 있으면 disabled 처리
                  InputProps={{
                    readOnly: isProjectMngIdReadOnly,
                    endAdornment: shouldShowGenerateButton && (
                      <InputAdornment position="end">
                        <Tooltip
                          title={!watchProjectTypeCd ? '프로젝트 구분을 먼저 선택해주세요' : ''}
                          arrow
                        >
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleGenerateProjectMngId}
                              disabled={isGeneratingId || !watchProjectTypeCd}
                            >
                              {isGeneratingId ? '생성 중...' : '생성'}
                            </Button>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          )}
          <Controller
            name="contractAmount"
            control={methods.control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                label={isAssignment ? '정부출연금' : '계약금액'}
                name="contractAmount"
                fullWidth
                value={field.value ? field.value.toLocaleString() : ''}
                placeholder={isAssignment ? '정부출연금' : '계약금액'}
                error={!!error}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  const numericValue = Number(value.replace(/,/g, ''));
                  if (!isNaN(numericValue)) {
                    field.onChange(numericValue);
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">원</InputAdornment>,
                  readOnly: readonly,
                }}
                helperText={
                  error
                    ? error?.message
                    : field.value
                    ? `${numberToSimpleKoreanCurrency(field.value)}`
                    : ''
                }
                inputRef={ref}
              />
            )}
          />
        </RowContainer>
      </StyledInnerFormContainer>

      {/* 기성내역: 과제가 아닌 경우 */}
      {!isAssignment && (
        <StyledInnerFormContainer>
          <StyledLabel>기성내역</StyledLabel>
          <ProjectRoundSalesSection methods={methods} readonly={readonly} />
        </StyledInnerFormContainer>
      )}

      {/* 입금내역: 과제인 경우 */}
      {isAssignment && (
        <StyledInnerFormContainer>
          <StyledLabel>입금내역</StyledLabel>
          <AssignDepositsSection methods={methods} readonly={readonly} />
        </StyledInnerFormContainer>
      )}

      {/* 매입내역 */}
      <StyledInnerFormContainer>
        <StyledLabel>매입내역</StyledLabel>
        {isAssignment ? (
          <PurchaseAssignmentSection methods={methods} readonly={readonly} />
        ) : (
          <PurchaseOtherSection methods={methods} readonly={readonly} />
        )}
      </StyledInnerFormContainer>

      {/* 비고/이슈 */}
      <RowContainer>
        <RHFTextField
          name="descr"
          label="비고/이슈"
          minRows={4}
          multiline
          inputProps={{ readOnly: readonly }}
        />
      </RowContainer>
    </StyledFormProvider>
  );
}

export default observer(ProjectForm);

const StyledFormProvider = styled(FormProvider)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(2),
}));

const RowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),

  '& > *': {
    flex: 1,
  },
}));

const StyledInnerFormContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const StyledLabel = styled('span')(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(0, 1),
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: '-12px',
  left: '20px',
  background: theme.palette.background.paper,
}));
